import axios from "axios";

const ASANA_BASE_URL = "https://app.asana.com/api/1.0";

// Get token from environment variables with REACT_APP_ prefix for Create React App
const ASANA_ACCESS_TOKEN = process.env.REACT_APP_ASANA_ACCESS_TOKEN;
const ASANA_BOARD_ID = process.env.REACT_APP_ASANA_BOARD_ID;
const ASANA_WORKSPACE_ID = process.env.REACT_APP_ASANA_WORKSPACE_ID;

// Create axios instance with proper headers
export const asanaClient = axios.create({
  baseURL: ASANA_BASE_URL,
  headers: {
    Authorization: `Bearer ${ASANA_ACCESS_TOKEN}`,
    "Content-Type": "application/json",
  },
  timeout: 15000, // Increased timeout for larger datasets
});

// Add response interceptor for better error handling
asanaClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log the error details
    if (error.response) {
      console.error("Asana API Error:", {
        status: error.response.status,
        data: error.response.data,
        endpoint: error.config.url,
      });
    } else {
      console.error("Asana API Error:", error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Tests authentication with Asana API
 * @returns {Promise<boolean>} True if authentication is successful
 */
export async function testAsanaAuth() {
  try {
    const response = await asanaClient.get("/users/me");
    return true;
  } catch (error) {
    console.error("Asana auth failed:", error.message);
    return false;
  }
}

/**
 * Cached custom field definitions
 * @type {Array}
 */
let cachedCustomFieldDefinitions = [];

/**
 * Fetches custom field settings from a project
 * @param {string} [projectId] - Optional project ID, defaults to env variable
 * @returns {Promise<Array>} Array of custom field definitions
 */
export async function fetchCustomFieldDefinitions(projectId = ASANA_BOARD_ID) {
  try {
    if (!projectId) {
      throw new Error("Project ID is required");
    }

    // If we have cached definitions, return them
    if (cachedCustomFieldDefinitions.length > 0) {
      return cachedCustomFieldDefinitions;
    }

    const response = await asanaClient.get(
      `/projects/${projectId}/custom_field_settings`,
      {
        params: {
          opt_fields:
            "custom_field,custom_field.resource_type,custom_field.name," +
            "custom_field.type,custom_field.enum_options,custom_field.gid," +
            "custom_field.enum_options.name,custom_field.enum_options.gid," +
            "custom_field.enum_options.color,is_important",
        },
      }
    );

    if (!response.data || !response.data.data) {
      throw new Error("Invalid response format from Asana");
    }

    // Transform the response into a more usable format
    cachedCustomFieldDefinitions = response.data.data
      .map((setting) => {
        const customField = setting.custom_field;
        if (!customField) {
          console.log("Skipping setting with no custom_field property");
          return null;
        }

        const result = {
          id: customField.gid,
          name: customField.name,
          type: customField.type,
          resourceType: customField.resource_type,
          enumOptions: customField.enum_options
            ? customField.enum_options.map((opt) => ({
                id: opt.gid,
                name: opt.name,
                color: opt.color || "none",
              }))
            : [],
          isImportant: setting.is_important || false,
        };

        return result;
      })
      .filter(Boolean); // Remove null values

    return cachedCustomFieldDefinitions;
  } catch (error) {
    console.error("Error fetching custom field definitions:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Status code:", error.response.status);
    }
    console.groupEnd();
    throw error;
  }
}
/**
 * Get a specific custom field definition by name (case insensitive)
 * @param {string} fieldName - The name of the custom field
 * @returns {Object|null} The custom field definition or null if not found
 */
export async function getCustomFieldByName(fieldName) {
  const definitions = await fetchCustomFieldDefinitions();
  return (
    definitions.find(
      (def) => def.name.toLowerCase() === fieldName.toLowerCase()
    ) || null
  );
}

/**
 * Fetches all sections in a project
 * @param {string} [projectId] - Optional project ID, defaults to env variable
 * @returns {Promise<Array>} Array of section objects
 */
export async function fetchSections(projectId = ASANA_BOARD_ID) {
  try {
    if (!projectId) {
      throw new Error("Project ID is required");
    }

    const response = await asanaClient.get(`/projects/${projectId}/sections`, {
      params: {
        opt_fields: "name,created_at",
      },
    });

    if (!response.data || !response.data.data) {
      throw new Error("Invalid response format from Asana");
    }

    return response.data.data.map((section) => ({
      id: section.gid,
      name: section.name,
      createdAt: section.created_at,
    }));
  } catch (error) {
    console.error("Error fetching sections:", error);
    throw error;
  }
}

/**
 * Extracts custom field values from an Asana task
 * @param {Object} task - The Asana task object
 * @returns {Object} Object with custom field values
 */
export function extractCustomFields(task) {
  if (!task.custom_fields || !Array.isArray(task.custom_fields)) {
    return {};
  }

  const result = {};

  for (const field of task.custom_fields) {
    if (!field.name) {
      continue;
    }

    let value = null;
    let valueWithColor = null;

    // Extract the value based on field type
    if (field.type === "enum") {
      if (field.enum_value) {
        value = field.enum_value.name;
        valueWithColor = {
          name: field.enum_value.name,
          color: field.enum_value.color || "none",
        };
      }
    } else if (field.type === "multi_enum") {
      if (field.multi_enum_values && field.multi_enum_values.length > 0) {
        // Simple value is just the names joined with commas
        value = field.multi_enum_values
          .map((enumVal) => enumVal.name)
          .join(", ");

        // Extended value includes both name and color
        valueWithColor = field.multi_enum_values.map((enumVal) => ({
          name: enumVal.name,
          color: enumVal.color || "none",
        }));
      }
    } else if (field.type === "number") {
      if (field.number_value !== undefined) {
        value = field.number_value;
      }
    } else if (field.type === "text") {
      if (field.text_value !== undefined) {
        value = field.text_value;
      }
    } else if (field.type === "date") {
      if (field.date_value) {
        value = field.date_value;
      }
    }

    // Check display_value as fallback
    if (value === null && field.display_value) {
      value = field.display_value;
    }

    // Add to result if we found a value
    if (value !== null) {
      // Normalize field name - trim extra spaces
      const fieldName = field.name.trim();

      // Store both the simple value and the value with color information
      result[fieldName] = value;

      // Store extended value with color information if available
      if (valueWithColor) {
        result[`${fieldName}_with_colors`] = valueWithColor;
      }
    }
  }

  // Special handling for Status field with space
  if (task.custom_fields) {
    const statusField = task.custom_fields.find(
      (field) =>
        (field.name === "Status" || field.name === "Status ") &&
        (field.type === "multi_enum" || field.type === "enum")
    );
    if (statusField && !result["Status"]) {
      // If we have multi_enum_values, use them
      if (
        statusField.multi_enum_values &&
        statusField.multi_enum_values.length > 0
      ) {
        // Simple string value
        result["Status"] = statusField.multi_enum_values
          .map((val) => val.name)
          .join(", ");

        // Extended value with colors
        result["Status_with_colors"] = statusField.multi_enum_values.map(
          (val) => ({
            name: val.name,
            color: val.color || "none",
          })
        );
      }
      // Otherwise fall back to display_value if available
      else if (statusField.display_value) {
        result["Status"] = statusField.display_value;
      }
    }
  }
  return result;
}

/**
 * Extract status values and colors from a multi-enum Status field
 * @param {Object} task - The raw task object from Asana
 * @returns {Object|null} Status object with name and color, or null if none
 */
export function getStatusWithColor(task) {
  // Safety check
  if (!task || !task.custom_fields || !Array.isArray(task.custom_fields)) {
    return null;
  }

  // Find the Status field with space at the end
  const statusField = task.custom_fields.find(
    (field) =>
      (field.name === "Status" || field.name === "Status ") &&
      (field.type === "multi_enum" || field.type === "enum")
  );

  if (!statusField) {
    return null;
  }

  // If it has multi_enum_values, extract the first one with its color
  if (
    statusField.multi_enum_values &&
    statusField.multi_enum_values.length > 0
  ) {
    const firstStatus = statusField.multi_enum_values[0];
    return {
      name: firstStatus.name,
      color: firstStatus.color || null,
    };
  }

  // If no value selected but has display_value, use that without color
  if (statusField.display_value) {
    return {
      name: statusField.display_value,
      color: null,
    };
  }

  // No status found
  return null;
}

/**
 * Fetches tasks from a specific section
 * @param {string} sectionId - The section ID to fetch tasks from
 * @param {Object} [options] - Additional options for the request
 * @param {number} [options.limit=100] - Maximum number of tasks to fetch
 * @param {string} [options.offset] - Pagination offset
 * @returns {Promise<Array>} Array of task objects
 */
export async function fetchTasksFromSection(sectionId, options = {}) {
  try {
    if (!sectionId) {
      throw new Error("Section ID is required");
    }

    const { limit = 100, offset } = options;

    const params = {
      limit,
      opt_fields:
        "name,completed,due_on,notes,custom_fields,custom_fields.gid," +
        "custom_fields.name,custom_fields.type,custom_fields.enum_value," +
        "custom_fields.enum_value.name,custom_fields.enum_value.gid," +
        "custom_fields.enum_value.color," +
        "custom_fields.multi_enum_values,custom_fields.multi_enum_values.name," +
        "custom_fields.multi_enum_values.color," +
        "custom_fields.number_value,custom_fields.text_value,custom_fields.date_value," +
        "custom_fields.display_value,memberships.section.name,permalink_url",
    };

    if (offset) {
      params.offset = offset;
    }

    const response = await asanaClient.get(`/sections/${sectionId}/tasks`, {
      params,
    });

    if (!response.data || !response.data.data) {
      throw new Error("Invalid response format from Asana");
    }

    // Extract the task data in a standard format
    const tasks = response.data.data.map((task) => {
      // Get custom fields
      const customFields = extractCustomFields(task);

      // Get status with color
      const statusWithColor = getStatusWithColor(task);
      if (statusWithColor) {
        customFields.Status = statusWithColor.name;
        if (statusWithColor.color) {
          customFields.StatusColor = statusWithColor.color;
        }
      }

      return {
        id: task.gid,
        name: task.name,
        completed: task.completed,
        dueDate: task.due_on,
        notes: task.notes,
        customFields,
        rawCustomFields: task.custom_fields,
        permalinkUrl: task.permalink_url,
      };
    });

    // Handle pagination if there are more results
    if (response.data.next_page) {
      const nextOffset = response.data.next_page.offset;
      const nextTasks = await fetchTasksFromSection(sectionId, {
        ...options,
        offset: nextOffset,
      });
      return [...tasks, ...nextTasks];
    }

    return tasks;
  } catch (error) {
    console.error(`Error fetching tasks from section ${sectionId}:`, error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Status code:", error.response.status);
    }
    throw error;
  }
}

/**
 * Fetches tasks from all sections in a project
 * @param {string} [projectId] - Optional project ID, defaults to env variable
 * @returns {Promise<Object>} Object with section names as keys and arrays of tasks as values
 */
export async function fetchTasksBySection(projectId = ASANA_BOARD_ID) {
  try {
    if (!projectId) {
      throw new Error("Project ID is required");
    }

    // First load all custom field definitions to help with task processing
    await fetchCustomFieldDefinitions(projectId);

    // Then fetch all sections
    const sections = await fetchSections(projectId);

    // Fetch tasks for each section in parallel
    const sectionTasks = await Promise.all(
      sections.map(async (section) => {
        const tasks = await fetchTasksFromSection(section.id);
        return {
          section: section.name,
          tasks,
        };
      })
    );

    // Convert to an object with section names as keys
    return sectionTasks.reduce((result, { section, tasks }) => {
      result[section] = tasks;
      return result;
    }, {});
  } catch (error) {
    console.error("Error fetching tasks by section:", error);
    throw error;
  }
}

/**
 * Fetches all tasks from a project (not organized by section)
 * @param {string} [projectId] - Optional project ID, defaults to env variable
 * @param {Object} [options] - Additional options for the request
 * @param {number} [options.limit=100] - Maximum number of tasks to fetch per page
 * @returns {Promise<Array>} Array of task objects
 */
export async function fetchAllTasks(projectId = ASANA_BOARD_ID, options = {}) {
  try {
    if (!projectId) {
      throw new Error("Project ID is required");
    }

    const { limit = 100 } = options;
    let allTasks = [];
    let offset = null;

    // First load all custom field definitions to help with task processing
    await fetchCustomFieldDefinitions(projectId);

    // Use pagination to fetch all tasks
    do {
      const params = {
        limit,
        opt_fields:
          "name,completed,due_on,notes,custom_fields,custom_fields.gid," +
          "custom_fields.name,custom_fields.type,custom_fields.enum_value," +
          "custom_fields.enum_value.name,custom_fields.enum_value.gid," +
          "custom_fields.enum_value.color," +
          "custom_fields.multi_enum_values,custom_fields.multi_enum_values.name," +
          "custom_fields.multi_enum_values.color," +
          "custom_fields.number_value,custom_fields.text_value,custom_fields.date_value," +
          "custom_fields.display_value,memberships.section.name,permalink_url",
      };

      if (offset) {
        params.offset = offset;
      }

      const response = await asanaClient.get(`/projects/${projectId}/tasks`, {
        params,
      });

      if (!response.data || !response.data.data) {
        throw new Error("Invalid response format from Asana");
      }

      const tasks = response.data.data.map((task) => {
        // Get custom fields
        const customFields = extractCustomFields(task);

        // Get status with color
        const statusWithColor = getStatusWithColor(task);
        if (statusWithColor) {
          customFields.Status = statusWithColor.name;
          if (statusWithColor.color) {
            customFields.StatusColor = statusWithColor.color;
          }
        }

        return {
          id: task.gid,
          name: task.name,
          completed: task.completed,
          dueDate: task.due_on,
          notes: task.notes,
          sectionName: task.memberships && task.memberships[0]?.section?.name,
          statusColor: statusWithColor?.color || null,
          customFields,
          rawCustomFields: task.custom_fields,
          permalinkUrl: task.permalink_url,
        };
      });

      allTasks = [...allTasks, ...tasks];

      // Check if there are more pages
      offset = response.data.next_page ? response.data.next_page.offset : null;
    } while (offset);

    return allTasks;
  } catch (error) {
    console.error("Error fetching all tasks:", error);
    throw error;
  }
}

/**
 * Extract status values from a multi-enum Status field
 * @param {Object} task - The raw task object from Asana
 * @returns {string|null} Comma-separated status values or null if none
 */
export function getMultiEnumStatus(task) {
  // Safety check
  if (!task || !task.custom_fields || !Array.isArray(task.custom_fields)) {
    return null;
  }

  // Find the Status field - note the space at the end of the name
  const statusField = task.custom_fields.find(
    (field) => field.name === "Status " && field.type === "multi_enum"
  );

  if (!statusField) {
    return null;
  }

  // If it has multi_enum_values, extract and join them
  if (
    statusField.multi_enum_values &&
    statusField.multi_enum_values.length > 0
  ) {
    return statusField.multi_enum_values.map((val) => val.name).join(", ");
  }

  // If no values selected but has display_value, use that
  if (statusField.display_value) {
    return statusField.display_value;
  }

  // No status values found
  return null;
}

/**
 * Searches for tasks across the workspace
 * @param {Object} filters - Search filters
 * @param {string} [workspaceId] - Optional workspace ID, defaults to env variable
 * @returns {Promise<Array>} Array of matching task objects
 */
export async function searchTasks(filters, workspaceId = ASANA_WORKSPACE_ID) {
  try {
    if (!workspaceId) {
      throw new Error("Workspace ID is required");
    }

    const params = {
      ...filters,
      opt_fields:
        "name,completed,due_on,notes,custom_fields,custom_fields.gid," +
        "custom_fields.name,custom_fields.type,custom_fields.enum_value," +
        "custom_fields.enum_value.name,custom_fields.enum_value.gid," +
        "custom_fields.number_value,custom_fields.text_value,custom_fields.date_value," +
        "memberships.section.name,memberships.project.name,permalink_url",
      limit: 100,
    };

    const response = await asanaClient.get(
      `/workspaces/${workspaceId}/tasks/search`,
      { params }
    );

    if (!response.data || !response.data.data) {
      throw new Error("Invalid response format from Asana");
    }

    return response.data.data.map((task) => ({
      id: task.gid,
      name: task.name,
      completed: task.completed,
      dueDate: task.due_on,
      notes: task.notes,
      sectionName: task.memberships && task.memberships[0]?.section?.name,
      projectName: task.memberships && task.memberships[0]?.project?.name,
      customFields: extractCustomFields(task),
      permalinkUrl: task.permalink_url,
    }));
  } catch (error) {
    console.error("Error searching tasks:", error);
    throw error;
  }
}

/**
 * Finds tasks with a specific custom field value
 * @param {Object} fieldInfo - Information about the field to match
 * @param {string} fieldInfo.name - The name of the custom field
 * @param {*} fieldInfo.value - The value to match
 * @param {string} [projectId] - Optional project ID, defaults to env variable
 * @returns {Promise<Array>} Array of matching task objects
 */
export async function findTasksByCustomField(
  fieldInfo,
  projectId = ASANA_BOARD_ID
) {
  try {
    // Get all tasks from the project
    const allTasks = await fetchAllTasks(projectId);

    // Filter tasks by custom field value
    return allTasks.filter((task) => {
      const fieldValue = task.customFields[fieldInfo.name];
      return fieldValue !== undefined && fieldValue === fieldInfo.value;
    });
  } catch (error) {
    console.error("Error finding tasks by custom field:", error);
    throw error;
  }
}

